/* eslint-disable no-console */

/**
 * Initializes Rollbar (error reporting service).
 *
 * Code was originally copied and modified from duolingo-web on 10/10/22
 * https://github.com/duolingo/duolingo-web/blob/284f64ca5078cba1479e1c2c735dac27a6c2af96/src/util/rollbar.ts
 *
 * PagerDuty Rollbar configuration for duolingo-web may be found at:
 * https://rollbar.com/duolingo/duolingo-web/settings/notifications/pagerduty/
 *
 * See https://github.com/rollbar/rollbar.js/tree/master/examples/webpack
 */

import type { Level, LogArgument } from "rollbar";
import Rollbar from "rollbar/src/browser/core";
import Instrumenter from "rollbar/src/browser/telemetry";
import scrub from "rollbar/src/scrub";
import Telemeter from "rollbar/src/telemetry";
import truncation from "rollbar/src/truncation";

import { getTimestampAfterSeconds } from "./datetime";
import { StorageKey, getItem, removeItem, setItem } from "./storage";

// If more than 10 errors occur in a 5-minute interval, stop tracking
// errors for the rest of the 5-minute interval. This is tracked across
// page loads (if localStorage is supported)
const THROTTLE_ERROR_COUNT_THRESHOLD = 10;
const THROTTLE_SAMPLE_INTERVAL_S = 5 * 60; // 5 minutes

interface RollbarThrottlingState {
  errorCount: number;
  firstErrorTimestamp: number;
}

/**
 * This determines if the current user is being throttled, and if not,
 * adds that there has been an error into the throttling state.
 */
const getIsThrottlingAndRecordError = () => {
  if (!(process.env.NODE_ENV === "production")) {
    // We don't want to drop errors during dev testing or internal
    // dogfooding
    return false;
  }

  try {
    let throttlingState = getItem(StorageKey.RollbarThrottling) as
      | RollbarThrottlingState
      | undefined;

    if (
      throttlingState === undefined ||
      throttlingState.firstErrorTimestamp <
        getTimestampAfterSeconds(-THROTTLE_SAMPLE_INTERVAL_S)
    ) {
      // Create a new state
      throttlingState = {
        errorCount: 1,
        firstErrorTimestamp: Date.now(),
      };
    } else {
      // Update existing state
      throttlingState.errorCount++;
    }

    setItem(StorageKey.RollbarThrottling, throttlingState);
    return throttlingState.errorCount > THROTTLE_ERROR_COUNT_THRESHOLD;
  } catch {
    // We got an error while handling an error :/. Let's reset
    // the throttling state (which will hopeful fix the storage issue),
    // and let the initial error be reported.
    try {
      removeItem(StorageKey.RollbarThrottling);
    } catch {
      // Super awkward, just ignore
    }

    return false;
  }
};

interface LogResult {
  /** Rollbar generates these client-side. */
  uuid: string;
}

/**
 * The payload of an item that can be sent to Rollbar.
 * Based on https://explorer.docs.rollbar.com/#operation/create-item.
 */
interface Payload {
  body?: {
    trace?: {
      exception: {
        class: string;
        description?: string;
        message?: string;
      };
      frames: { filename: string }[];
    };
  };
  client?: {
    javascript?: {
      code_version: string;
      guess_uncaught_frames: boolean;
      source_map_enabled: boolean;
    };
  };
  custom?: {
    isUncaught?: boolean;
    request?: unknown;
  };
  environment?: "production" | string;
  level?: Level;
  person?: {
    id: string;
    username?: string;
  };
  /** Rollbar generates these client-side. */
  uuid?: string;
}

type ErrorHandler = (item: Payload) => void;

let onChunkLoadError: ErrorHandler | null = null;
let onUncaughtError: ErrorHandler | null = null;

export const setOnChunkLoadError = (fn: ErrorHandler) => {
  onChunkLoadError = fn;
};

export const setOnUncaughtError = (fn: ErrorHandler) => {
  onUncaughtError = fn;
};

const DUOLINGO_DOMAINS = [
  "duolingo.com",
  "localhost",
  "webpack://",
  "webpack-internal://",
];

/** We ignore server errors, rate limits, and timeouts */
export const UNACTIONABLE_HTTP_STATUS_CODES = [429, 500, 502, 503, 504];

const MAX_NUM_UNCAUGHT_ITEMS = 5;

let numReportedItems = 0;
let numUncaughtItems = 0;
let rollbar: Rollbar;

// https://github.com/rollbar/rollbar.js/pull/817
Rollbar.setComponents({
  instrumenter: Instrumenter,
  scrub,
  telemeter: Telemeter,
  truncation,
});

export const init = () =>
  (rollbar = Rollbar.init({
    /** 'post_client_item' token */
    accessToken: "6cd0d499969e4a7a9e35cbe749fdf5dc",
    autoInstrument: {
      // We patch the console ourselves for more control over what to include.
      log: false,
      navigation: true,
    },
    captureUncaught: true,
    captureUnhandledRejections: true,
    checkIgnore: (isUncaught, args, item: Payload) => {
      try {
        // Ignore errors that occur while the user is offline
        if (!navigator.onLine) {
          return true;
        }

        if (item?.body?.trace) {
          // Ignore errors that originate from outside of our JS.
          const topFrameFilename = item.body.trace.frames.slice(-1)[0].filename;
          if (!new RegExp(DUOLINGO_DOMAINS.join("|")).test(topFrameFilename)) {
            return true;
          }

          // Chunk load errors could indicate that something went wrong
          // during deployment, but mostly they are network errors on
          // the user's end.
          if (
            /Loading( CSS)? chunk .+? failed\./.test(
              item.body.trace.exception.message ?? "",
            )
          ) {
            onChunkLoadError?.(item);
            return true;
          }
        }

        if (getIsThrottlingAndRecordError()) {
          console.log("Ignoring because throttling limit reached:", item);
          return true;
        }

        if (isUncaught) {
          // Handle uncaught errors if they passed ignore criteria.
          // If the error won't be reported to Rollbar because we
          // have already reported the maximum number of uncaught
          // items, it is handled here nonetheless.
          onUncaughtError?.(item);

          if (numUncaughtItems >= MAX_NUM_UNCAUGHT_ITEMS) {
            console.log("Ignoring because item reporting limit reached:", item);
            return true;
          }
          numUncaughtItems++;

          item.custom = item.custom ?? {};
          item.custom.isUncaught = true;
        }
      } catch (ex) {
        // Catch-all to prevent errors from causing infinite loops.
        console.error(ex);
      }
      return false;
    },
    hostBlockList: [],
    hostSafeList: DUOLINGO_DOMAINS,
    ignoredMessages: ["script error"],
    payload: {
      client: {
        javascript: {
          code_version: "1",
          guess_uncaught_frames: true,
          source_map_enabled: true,
        },
      },
      environment: process.env.NODE_ENV,
    } as Payload,
    verbose: process.env.NODE_ENV === "development",
    // The default value is documented as `true` but is actually `false`.
    // Repeating it here for clarity. If enabled, `wrapGlobals` should
    // be added in `setComponents`.
    wrapGlobalEventHandlers: false,
  }));

type WrappedFn = (
  /** Rollbar item title */
  errorOrTitle: Error | string,
  /** Custom reporting options */
  options?: {
    /** Custom data to report */
    data?: object | string;
    /** `Error` instance to report */
    ex?: Error;
    /** Whether this message should be reported at most once per page load */
    once?: boolean;
    /** A number in the range [0, 1] */
    sample?: number;
  },
) => LogResult | undefined;

/**
 * We wrap Rollbar's reporting functions for two reasons:
 *
 * 1. To add custom options like sampling rate and report-only-once
 *
 * 2. To expose a saner API. Rollbar's reporting functions are magical black
 * boxes that accept unordered, untyped, and unbounded parameter lists
 *
 */
const wrapRollbarFn =
  (
    fn: (...args: LogArgument[]) => LogResult,
    maxItemsToReport = 5,
  ): WrappedFn =>
  (errorOrTitle, options = {}) => {
    let message =
      errorOrTitle instanceof Error
        ? [errorOrTitle.name, errorOrTitle.message]
            .filter(part => part)
            .join(": ")
        : errorOrTitle;

    // Rate limit our own Rollbar reporting (we used to use the Rollbar library's
    // `itemsPerMinute` and `maxItems` config options, but they don't seem to work)
    if (numReportedItems >= maxItemsToReport) {
      console.log("Ignoring because item reporting limit reached:", message);
      return undefined;
    }

    const { data, ex, sample = 1 } = options;

    // Support `sample` option
    if (sample < 1) {
      if (Math.random() > sample) {
        console.log("Ignoring due to sampling:", message);
        return undefined;
      }
      message = `${message} (sampled at ${sample * 100}%)`;
    }

    const rollbarArgs: LogArgument[] = [message];
    if (errorOrTitle instanceof Error) {
      rollbarArgs.push(errorOrTitle);
    }

    // Support `data` option
    if (typeof data === "object" || typeof data === "string") {
      rollbarArgs.push(data);
    }

    // Support `ex` option
    if (!(errorOrTitle instanceof Error) && ex !== undefined) {
      // We check that ex is actually an error in case it was
      // passed in as any or cast to an Error.
      if (ex instanceof Error) {
        rollbarArgs.push(ex);
      } else {
        console.error("Tried to report non-Error as Error:", ex);
      }
    }

    numReportedItems += 1;
    return fn(...rollbarArgs);
  };

/** Adds a log message to the telemetry. */
export const captureLog = (message: string, level: Level) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (rollbar.captureEvent as any)("log", { message }, level);

/** Adds a navigation event to the telemetry. */
export const captureNavigation = (from: string, to: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (rollbar.captureEvent as any)("navigation", { from, to }, "info");

/** Reports a message to Rollbar at log level "error". */
export const error: WrappedFn = (errorOrTitle, options) =>
  wrapRollbarFn(rollbar.error.bind(rollbar))(errorOrTitle, options);

/** Reports a message to Rollbar at log level "info". */
export const info: WrappedFn = (errorOrTitle, options) =>
  wrapRollbarFn(rollbar.info.bind(rollbar))(errorOrTitle, options);

/** Reports a message to Rollbar at log level "warning". */
export const warning: WrappedFn = (errorOrTitle, options) =>
  wrapRollbarFn(rollbar.warning.bind(rollbar))(errorOrTitle, options);
